import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const taskKeys = {
  all: ['tasks'],
  lists: () => [...taskKeys.all, 'list'],
  details: () => [...taskKeys.all, 'detail'],
  detail: (id) => [...taskKeys.details(), id],
  byTemplate: (templateId) => [...taskKeys.all, 'template', templateId],
};

/**
 * Hook para obtener todas las tareas
 */
export const useTasks = () => {
  return useQuery({
    queryKey: taskKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('tasks');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una tarea específica
 * @param {string} id - UUID de la tarea
 * @param {Object} options - Opciones adicionales para la query
 */
export const useTask = (id, options = {}) => {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`tasks/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener tareas por plantilla
 * @param {string} templateId - UUID de la plantilla
 * @param {Object} options - Opciones adicionales para la query
 */
export const useTasksByTemplate = (templateId, options = {}) => {
  return useQuery({
    queryKey: taskKeys.byTemplate(templateId),
    queryFn: async () => {
      const response = await apiClient.get(`tasks/by-template/${templateId}`);
      return response.data;
    },
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva tarea
 */
export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskData) => {
      const response = await apiClient.post('tasks', taskData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      if (data.uuid_task_template) {
        queryClient.invalidateQueries({ queryKey: taskKeys.byTemplate(data.uuid_task_template) });
      }
    },
    onError: (error) => {
      console.error('Error creating task:', error);
    },
  });
};

/**
 * Hook para actualizar una tarea
 */
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`tasks/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(taskKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      if (data.uuid_task_template) {
        queryClient.invalidateQueries({ queryKey: taskKeys.byTemplate(data.uuid_task_template) });
      }
    },
    onError: (error) => {
      console.error('Error updating task:', error);
    },
  });
};

/**
 * Hook para eliminar una tarea
 */
export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`tasks/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      if (data.uuid_task_template) {
        queryClient.invalidateQueries({ queryKey: taskKeys.byTemplate(data.uuid_task_template) });
      }
      queryClient.removeQueries({ queryKey: taskKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting task:', error);
    },
  });
};

/**
 * Hook para reordenar tareas en una plantilla
 */
export const useReorderTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, taskOrders }) => {
      const response = await apiClient.put(`tasks/reorder/${templateId}`, {
        task_orders: taskOrders
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.byTemplate(variables.templateId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
    onError: (error) => {
      console.error('Error reordering tasks:', error);
    },
  });
};

/**
 * Hook para duplicar tareas entre plantillas
 */
export const useDuplicateTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceTemplateId, targetTemplateId }) => {
      const response = await apiClient.post(`tasks/duplicate/${sourceTemplateId}/${targetTemplateId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.byTemplate(variables.targetTemplateId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
    onError: (error) => {
      console.error('Error duplicating tasks:', error);
    },
  });
};